import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import Signin from "../components/Landing/Signin";
import Overlay from "../components/Landing/Overlay";

const SigninModalContext = createContext(null);

export function useSigninModal() {
  const ctx = useContext(SigninModalContext);
  if (!ctx) {
    throw new Error("useSigninModal must be used within SigninModalProvider");
  }
  return ctx;
}

export function useSigninModalOptional() {
  return useContext(SigninModalContext);
}

export function SigninModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const menuCloseRef = useRef(null);
  const { email } = useSelector((state) => state.auth);

  const registerMenuClose = useCallback((fn) => {
    menuCloseRef.current = fn;
    return () => {
      if (menuCloseRef.current === fn) {
        menuCloseRef.current = null;
      }
    };
  }, []);

  const openSignin = useCallback(() => {
    if (menuCloseRef.current) {
      menuCloseRef.current();
    }
    setIsOpen(true);
    document.body.classList.add("no-scroll");
  }, []);

  const closeSignin = useCallback(() => {
    setIsOpen(false);
    document.body.classList.remove("no-scroll");
  }, []);

  useEffect(() => {
    if (email && isOpen) {
      closeSignin();
    }
  }, [email, isOpen, closeSignin]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      openSignin,
      closeSignin,
      registerMenuClose,
    }),
    [isOpen, openSignin, closeSignin, registerMenuClose]
  );

  return (
    <SigninModalContext.Provider value={value}>
      {children}
      {!email && isOpen && (
        <>
          <Overlay isOpen={isOpen} closeModal={closeSignin} />
          <Signin isOpen={isOpen} closeModal={closeSignin} />
        </>
      )}
    </SigninModalContext.Provider>
  );
}

export default SigninModalContext;
